import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { IMAGES } from "@/lib/constants";
import { Award, BookOpen, Compass, Hammer, Leaf, ShieldCheck } from "lucide-react";

const VALUES = [
  { icon: Compass, title: "Systems thinking", body: "Cleanability is upstream of operations. We work at the level of structure, material, and decision." },
  { icon: ShieldCheck, title: "Scientific rigour", body: "Evidence-led research, peer review, reproducibility. No anecdotes dressed as standards." },
  { icon: Leaf, title: "Sustainability", body: "Chemicals, water, embodied carbon and biodiversity are first-class cleanability variables." },
  { icon: Award, title: "Human dignity", body: "The people who clean civilisation deserve a discipline that takes them seriously." },
  { icon: BookOpen, title: "Open scholarship", body: "Research belongs in the world. Publications are open where possible, citable always." },
  { icon: Hammer, title: "Practitioner clarity", body: "Theory must reach the people specifying, building, and operating the spaces in question." },
];

const TIMELINE = [
  { year: "2018", text: "Fuseini Dawuda articulates the Cleanability Index in early operational papers." },
  { year: "2021", text: "Publication of The Whisper of Brooms; cleanability begins as a public conversation." },
  { year: "2022", text: "The Mop and the Meaning establishes the dignity argument in operational thought." },
  { year: "2023", text: "Mastering Soft FM systematises practice across emerging cleanability cohorts." },
  { year: "2024", text: "The Cleanability Institute is formally constituted as an independent body." },
  { year: "2025", text: "Healthcare Cleanability Standards v2.0; CleanabilityIQ enters institutional pilots." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        testId="about-hero"
        eyebrow="The Institute"
        title="An independent body for an unwritten discipline."
        lead="The Cleanability Institute exists to formalise, measure, teach and propagate the science of cleanability across the built environment — and to defend the dignity of the people who maintain it."
      />

      {/* mission */}
      <section className="container-x section-y border-t border-border">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <SectionEyebrow>Mission</SectionEyebrow>
          </div>
          <div className="lg:col-span-8 space-y-6">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium text-balance">
              To make cleanability a measurable, teachable, fundable discipline.
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              We exist to advance cleanability science, develop globally credible standards, train a
              new generation of professionals, and ensure the built environment is designed for the
              decades after the photograph is taken.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Our work spans research, publication, accreditation, standards, intelligence platforms,
              and institutional partnerships across more than thirty countries.
            </p>
          </div>
        </div>
      </section>

      {/* founder */}
      <section className="border-y border-border bg-secondary/30">
        <div className="container-x section-y">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal>
              <div className="lg:col-span-5 relative">
                <div className="relative rounded-2xl overflow-hidden border border-border max-w-md">
                  <img src={IMAGES.human1} alt="Founder" className="w-full h-auto" />
                </div>
              </div>
            </Reveal>
            <div className="lg:col-span-7">
              <SectionEyebrow>Founder</SectionEyebrow>
              <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">
                Fuseini Dawuda
              </h2>
              <p className="mt-2 text-muted-foreground">Facilities management theorist · Author · Operational systems thinker</p>
              <div className="mt-6 space-y-5 text-base md:text-lg leading-relaxed text-muted-foreground">
                <p>
                  Fuseini Dawuda is the founder of the Cleanability Institute and the creator of the
                  Cleanability Index. He has spent two decades inside the operational realities of
                  buildings — wards, terminals, factories, plazas, classrooms — and the boardrooms
                  that govern them.
                </p>
                <p>
                  His work argues that maintenance is not the residue of architecture but its
                  unwritten companion. He is the author of <em className="text-foreground">Conceptualizing
                  Cleanability</em>, <em className="text-foreground">Mastering Soft FM</em>, <em className="text-foreground">The Mop and the Meaning</em>,
                  and <em className="text-foreground">The Whisper of Brooms</em>.
                </p>
                <p>
                  He advocates for invisible labour and humane infrastructure as foundational concerns
                  in every modern building brief.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* values */}
      <section className="container-x section-y">
        <SectionEyebrow>Values</SectionEyebrow>
        <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">Six commitments that shape every project.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.05}>
              <div data-testid={`value-${i}`} className="rounded-2xl border border-border bg-card p-8 hover-lift h-full">
                <v.icon className="h-5 w-5 text-[hsl(var(--accent))]" />
                <div className="mt-6 font-display text-lg font-medium tracking-tight">{v.title}</div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* timeline */}
      <section className="border-t border-border bg-secondary/30">
        <div className="container-x section-y">
          <SectionEyebrow>Timeline</SectionEyebrow>
          <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">From a quiet idea to a global discipline.</h2>
          <ol className="mt-12 relative border-l border-border ml-3">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.05}>
                <li data-testid={`timeline-${i}`} className="relative pl-8 pb-10 last:pb-0">
                  <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-[hsl(var(--accent))]" />
                  <div className="font-mono-mini text-muted-foreground">{t.year}</div>
                  <div className="mt-2 text-base md:text-lg max-w-2xl">{t.text}</div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* leadership */}
      <section className="container-x section-y">
        <SectionEyebrow>Leadership & advisory</SectionEyebrow>
        <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">A board being assembled across disciplines.</h2>
        <p className="mt-4 text-muted-foreground max-w-2xl">
          We are convening senior architects, hospital epidemiologists, FM directors, materials scientists,
          and ethicists. Advisory positions are being announced through 2025–26.
        </p>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {["Architecture", "Healthcare", "Manufacturing", "Public sector", "AI & data", "Standards", "Materials", "Ethics"].map((d, i) => (
            <div key={d} data-testid={`advisory-${i}`} className="rounded-xl border border-border bg-card p-5 hover-lift">
              <div className="font-mono-mini text-muted-foreground">Advisor</div>
              <div className="mt-2 font-display font-medium">{d}</div>
              <div className="text-xs text-muted-foreground mt-1">To be announced</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
